import React, { MouseEvent, useRef, useState } from 'react';
import { useModal } from 'react-modal-hook';
import styled from 'styled-components';
import { AlertContent, Button, CentBasedInputCurrency, H2, Label, ModalContent, ModalFactory, ModalFooter, ModalHeader, ModalLayout } from 'components';
import { theme, toRem } from 'design';
import { ITipCalculatorPayee } from 'types';

export interface IHoldbackModalProps {
  children: React.ReactNode;
  className?: string;
  defaultValue?: number;
  payee: ITipCalculatorPayee;
  disabled?: boolean;
  onChange: (holdback: number) => void;
}

const HoldbackFieldContainer = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  padding: ${toRem(16)};
  background: ${theme.color.neutral[50]};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${theme.borderRadius.small}px;

  label {
    color: ${theme.color.neutral[500]};
    font-size: ${toRem(12)};
    margin-bottom: ${toRem(8)};
  }
`;

const HoldbackModalFooter = styled(ModalFooter)`
  padding-top: ${toRem(24)};

  button:first-child,
  button:last-child {
    justify-self: stretch;
  }
`;

const HoldbackModalHeader = styled(ModalHeader)`
  padding-bottom: ${toRem(24)};
`;

const HoldbackModalLayout = styled(ModalLayout)`
  width: 100%;
  max-width: ${toRem(512)};
`;

const HoldbackModalTrigger = styled.button``;

const PayeeInfo = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: ${toRem(16)};
  text-align: center;

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-flow: row nowrap;
  }
`;

const PayeeName = styled.span`
  font-size: ${toRem(16)};
  font-weight: ${theme.font.weight.bold};
  color: ${theme.color.neutral[900]};
  text-align: left;
`;

const PayeeId = styled.span`
  font-size: ${toRem(11)};
  font-weight: ${theme.font.weight.regular};
  color: ${theme.color.neutral[500]};
  text-transform: uppercase;
  word-break: break-all;
  text-align: right;
`;

const HoldbackModal: React.FC<IHoldbackModalProps> = ({
  children,
  className = '',
  /**
   * the default value (in cents) to set as the default value
   * of the input field.
   */
  defaultValue,
  payee,
  disabled,
  onChange,
}) => {
  const [value, setValue] = useState(defaultValue || 0);
  const dataCy = useRef('holdback-modal').current;

  const onHoldbackChange = (value: string) => {
    const parsedValue = parseInt(value, 10);
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(parsedValue)) return;

    setValue(parsedValue);
  };

  const onSave = () => {
    onChange(value);
    hideModal();
  };

  const [showModal, hideModal] = useModal(() => {
    const onClose = () => {
      setValue(defaultValue || 0);
      hideModal();
    };

    return (
      <ModalFactory customModal onClose={hideModal} isActive>
        {disabled ? (
          <AlertContent
            title="You are not authorized to edit holdback amounts."
            message="Contact your manager or Kickfin support for assistance."
            rightActionLabel="Go Back"
            onClose={onClose}
            onRightAction={onClose}
            hideLeftAction
            maxWidth={440}
            dataCy={dataCy}
          />
        ) : (
          <HoldbackModalLayout isActive hideCloseButton onClose={onClose} data-cy={dataCy}>
            <HoldbackModalHeader>
              <H2 data-cy={`${dataCy}-header`} fontWeight="bold">
                Edit Holdback
              </H2>
            </HoldbackModalHeader>
            <ModalContent>
              <HoldbackFieldContainer>
                <PayeeInfo>
                  <PayeeName data-cy={`${dataCy}-payee-name`}>{payee.name}</PayeeName>
                  {payee.provider?.customerEmployeeId && <PayeeId data-cy={`${dataCy}-payee-id`}>ID: {payee.provider.customerEmployeeId}</PayeeId>}
                </PayeeInfo>
                <Label>Holdback</Label>
                <CentBasedInputCurrency data-cy={`${dataCy}-currency-input`} value={value.toString()} onChange={onHoldbackChange} allowZero />
              </HoldbackFieldContainer>
            </ModalContent>
            <HoldbackModalFooter columns={2}>
              <Button data-cy="cancel-holdback-btn" appearance="white" label="Cancel" onClick={onClose} />
              <Button data-cy="save-holdback-btn" label="Save" onClick={onSave} />
            </HoldbackModalFooter>
          </HoldbackModalLayout>
        )}
      </ModalFactory>
    );
  }, [value, payee, onHoldbackChange, onSave]);

  const onTriggerClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    showModal();
  };

  return (
    <HoldbackModalTrigger className={className} onClick={onTriggerClick}>
      {children}
    </HoldbackModalTrigger>
  );
};

export default HoldbackModal;
